import "./lfo-module.css";

import { Display } from "./display.ts";
import { Dropdown } from "./dropdown.ts";
import { GLYPHS, Selector } from "./selector.ts";
import { Knob } from "./knob.ts";
import { ToggleSwitch } from "./toggle-switch.ts";
import {
  MOD_DEST_ENGINE_INDEX,
  MOD_DEST_OPTIONS,
  type ModDestId,
} from "./mod-env-module.ts";
import { morphedSample } from "./morph-shared.ts";

// LfoModule — free-routing LFO. Mirrors `ModEnvModule` for the
// destination + amount controls, adds LFO-specific bits:
//
//   • shape selector (sine / triangle / square / saw — snaps the engine's
//     continuous shape param to the four archetypes)
//   • sync toggle (off = free Hz rate, on = tempo-locked subdivision)
//   • rate knob that switches semantics with `sync` — when Hz, the knob
//     is continuous 0.01..30 Hz with log taper; when sync, the knob is
//     stepped through the six `LfoRateSync` subdivisions
//   • amount knob (bipolar −100% .. +100%, same as mod env)
//
// A small live preview canvas at the top animates the actual LFO shape
// scrolling left-to-right so the user can see rate / shape changes
// before pressing a note.

/** LFO shape archetype values mapped onto the engine's continuous
 *  `lfo.shape` morph (0..1). Same archetype mapping as the oscillator
 *  morph strip, just snapped instead of continuous. */
const LFO_SHAPES: { value: string; glyph: typeof GLYPHS.sine; morph: number }[] = [
  { value: "sine", glyph: GLYPHS.sine, morph: 0.0 },
  { value: "triangle", glyph: GLYPHS.triangle, morph: 1 / 3 },
  { value: "square", glyph: GLYPHS.square, morph: 2 / 3 },
  { value: "saw", glyph: GLYPHS.saw, morph: 1.0 },
];

/** Tempo-sync subdivisions. Order MUST match the engine's `LfoRateSync`
 *  variants — the knob's snapped value becomes the discrete index sent
 *  via `setDiscrete("lfo.rate_sync", ...)`. */
const SYNC_LABELS = ["1/1", "1/2", "1/4", "1/8", "1/16", "1/32"];
const SYNC_DEFAULT_INDEX = 2; // 1/4 — matches engine default

export type LfoShape = "sine" | "triangle" | "square" | "saw";

export interface LfoState {
  shape: LfoShape;
  rateHz: number;
  rateSyncIndex: number;
  sync: boolean;
  amount: number;
  destination: ModDestId;
}

export interface LfoModuleOptions {
  container: HTMLElement;
  setParam: (path: string, value: number) => void;
  setDiscrete: (path: string, value: number) => void;
  initial?: Partial<LfoState>;
}

export class LfoModule {
  private root: HTMLDivElement;
  private rateHost: HTMLDivElement;
  private destDropdown!: Dropdown<ModDestId>;
  private shapeSelector!: Selector<string>;
  private syncToggle!: ToggleSwitch;
  private amountKnob!: Knob;
  /** Current snapped shape morph value (0..1) — drives the preview's
   *  morphedSample call so the canvas shows whichever waveform the user
   *  selected, not an interpolation. */
  private shapeMorph: number;
  /** Current Hz value when sync is off, current sync index when on.
   *  Kept here so toggling the sync switch can re-target the rate knob
   *  to the appropriate range / format without losing the user's prior
   *  setting in the other mode. */
  private rateHz: number;
  private rateSyncIndex: number;
  private sync: boolean;
  /** Phase accumulator for the live preview, ticked off requestAnimationFrame. */
  private previewPhase = 0;
  private previewLastT = 0;
  private setParam: LfoModuleOptions["setParam"];
  private setDiscrete: LfoModuleOptions["setDiscrete"];

  constructor(opts: LfoModuleOptions) {
    const init = opts.initial ?? {};
    const initShape = init.shape ?? "sine";
    this.shapeMorph =
      LFO_SHAPES.find((s) => s.value === initShape)?.morph ?? 0;
    this.rateHz = init.rateHz ?? 1;
    this.rateSyncIndex = init.rateSyncIndex ?? SYNC_DEFAULT_INDEX;
    this.sync = init.sync ?? false;
    this.setParam = opts.setParam;
    this.setDiscrete = opts.setDiscrete;

    this.root = document.createElement("div");
    this.root.className = "lfo-module";

    // Header — title + destination dropdown.
    const header = document.createElement("div");
    header.className = "lfo-module-header";
    const title = document.createElement("span");
    title.className = "lfo-module-title";
    title.textContent = "lfo";
    header.append(title);

    const destHost = document.createElement("div");
    destHost.className = "lfo-module-dest";
    header.append(destHost);
    this.destDropdown = new Dropdown<ModDestId>({
      container: destHost,
      label: "dest",
      options: MOD_DEST_OPTIONS,
      value: init.destination ?? "none",
      minWidth: 180,
      onChange: (value) => {
        this.setDiscrete("lfo.destination", MOD_DEST_ENGINE_INDEX[value]);
      },
    });
    this.root.append(header);

    // Live preview — uses the existing Display component (recessed
    // well, DPR-safe canvas, raf loop).
    const previewHost = document.createElement("div");
    previewHost.className = "lfo-module-preview";
    this.root.append(previewHost);
    new Display({
      container: previewHost,
      width: 360,
      height: 64,
      fluid: true,
      onFrame: (ctx, w, h) => this.drawPreview(ctx, w, h),
    });

    // Shape selector + sync toggle on the same row.
    const shapeRow = document.createElement("div");
    shapeRow.className = "lfo-module-shape-row";
    this.root.append(shapeRow);
    this.shapeSelector = new Selector<string>({
      container: shapeRow,
      options: LFO_SHAPES.map((s) => ({
        value: s.value,
        glyph: s.glyph,
        label: s.value,
      })),
      value: initShape,
      onChange: (value) => {
        const morph = LFO_SHAPES.find((s) => s.value === value)?.morph ?? 0;
        this.shapeMorph = morph;
        this.setParam("lfo.shape", morph);
      },
    });
    const syncHost = document.createElement("div");
    shapeRow.append(syncHost);
    this.syncToggle = new ToggleSwitch({
      container: syncHost,
      label: "sync",
      active: this.sync,
      onToggle: (active) => {
        this.sync = active;
        this.setDiscrete("lfo.rate_mode", active ? 1 : 0);
        this.refreshRateKnob();
      },
    });

    // Knob row — rate (Hz or sync) | amount.
    const knobs = document.createElement("div");
    knobs.className = "lfo-module-knobs";
    this.root.append(knobs);

    this.rateHost = document.createElement("div");
    knobs.append(this.rateHost);
    // The knob is rebuilt on sync-toggle (instead of swapping ranges in
    // place) because Knob doesn't currently support a runtime min/max
    // change; cheaper than adding that API for one consumer.
    this.buildRateKnob(this.rateHost);

    const amountHost = document.createElement("div");
    amountHost.className = "lfo-module-amount";
    knobs.append(amountHost);
    this.amountKnob = new Knob({
      container: amountHost,
      size: 44,
      min: -1,
      max: 1,
      default: 0,
      value: init.amount ?? 0,
      label: "mod amt",
      format: (v) => {
        const pct = Math.round(v * 100);
        return pct > 0 ? `+${pct}%` : `${pct}%`;
      },
      onChange: (v) => this.setParam("lfo.amount", v),
    });

    opts.container.append(this.root);
  }

  getState(): LfoState {
    return {
      shape: (this.shapeSelector.getValue() as LfoShape) ?? "sine",
      rateHz: this.rateHz,
      rateSyncIndex: this.rateSyncIndex,
      sync: this.syncToggle.isActive(),
      amount: this.amountKnob.current,
      destination: this.destDropdown.getValue() ?? "none",
    };
  }

  setState(s: LfoState): void {
    this.shapeSelector.select(s.shape);
    // Sync state has to be applied AFTER stored rateHz / rateSyncIndex
    // values so the rebuilt rate knob picks up the correct numeric
    // value for whichever mode is now active.
    this.rateHz = s.rateHz;
    this.rateSyncIndex = s.rateSyncIndex;
    // Trigger the rate-mode → setDiscrete + rate-knob rebuild path via
    // the toggle switch; if the value didn't change, manually rebuild
    // anyway so the knob picks up the new stored numerics.
    if (this.syncToggle.isActive() !== s.sync) {
      this.syncToggle.setActive(s.sync);
    } else {
      // Same mode but rateHz / rateSyncIndex may have changed; rebuild
      // the knob to reflect the new stored values.
      this.refreshRateKnob();
    }
    // The rebuilt knob's onChange already pushed the current value to
    // the engine via the knob constructor's value field — but we still
    // need to push it explicitly here in case the value matched the
    // default and the constructor's onChange wasn't fired.
    if (s.sync) {
      this.setDiscrete("lfo.rate_sync", s.rateSyncIndex);
    } else {
      this.setParam("lfo.rate_hz", s.rateHz);
    }
    this.amountKnob.setValue(s.amount);
    this.destDropdown.setValue(s.destination);
  }

  /** Construct (or re-construct) the rate knob in the supplied host. The
   *  knob's range and format depend on whether `this.sync` is set — Hz
   *  mode is continuous log-taper, sync mode is six discrete steps. */
  private buildRateKnob(host: HTMLElement): void {
    host.replaceChildren();
    if (this.sync) {
      new Knob({
        container: host,
        size: 44,
        min: 0,
        max: SYNC_LABELS.length - 1,
        default: SYNC_DEFAULT_INDEX,
        value: this.rateSyncIndex,
        step: 1,
        label: "rate",
        format: (v) => SYNC_LABELS[Math.round(v)] ?? "?",
        onChange: (v) => {
          const idx = Math.round(v);
          this.rateSyncIndex = idx;
          this.setDiscrete("lfo.rate_sync", idx);
        },
      });
      return;
    }
    new Knob({
      container: host,
      size: 44,
      min: 0.01,
      max: 30,
      default: 1,
      value: this.rateHz,
      taper: "log",
      label: "rate",
      format: (v) => (v < 1 ? `${v.toFixed(2)} hz` : `${v.toFixed(1)} hz`),
      onChange: (v) => {
        this.rateHz = v;
        this.setParam("lfo.rate_hz", v);
      },
    });
  }

  /** Sync toggle changed — rebuild the rate knob so its range / format
   *  matches the active mode. The Knob component is light enough that
   *  rebuilding is cheaper than adding a setRange API just for this. */
  private refreshRateKnob(): void {
    this.buildRateKnob(this.rateHost);
  }

  destroy(): void {
    this.root.remove();
  }

  private drawPreview(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
  ): void {
    // Advance the preview phase based on the current effective rate.
    // The preview scrolls at a perceptually slowed-down rate so even
    // fast LFOs (e.g. 20 Hz) read as a visible cycle rather than a
    // blurred mess. Real audio uses the actual rate; this is just for
    // visualisation.
    const now = performance.now();
    const dt = this.previewLastT > 0 ? (now - this.previewLastT) / 1000 : 0;
    this.previewLastT = now;
    // Visual rate caps so the preview stays readable. Sync mode shows
    // the subdivision as if BPM were 120 (so user sees "1/4 at 120 bpm
    // = 2 Hz"-ish motion).
    const visualHz = this.sync
      ? syncToHzAt120(this.rateSyncIndex)
      : this.rateHz;
    this.previewPhase = (this.previewPhase + dt * visualHz) % 1;

    ctx.clearRect(0, 0, w, h);
    // Centerline.
    ctx.save();
    ctx.strokeStyle = "rgba(56, 189, 248, 0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();
    ctx.restore();

    // Draw 2 cycles across the canvas, scrolling with phase.
    ctx.save();
    ctx.shadowColor = "rgba(56, 189, 248, 0.55)";
    ctx.shadowBlur = 10;
    ctx.strokeStyle = "rgba(125, 211, 252, 0.9)";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    const cycles = 2;
    const steps = Math.max(2, Math.floor(w * 2));
    for (let i = 0; i <= steps; i++) {
      const u = i / steps;
      const phaseAt = (u * cycles + this.previewPhase) % 1;
      const v = morphedSample(phaseAt, this.shapeMorph * 3); // 0..1 → 0..3
      const x = u * w;
      const y = h / 2 - v * (h * 0.42);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.restore();
  }
}

/** Cycles per second a given sync subdivision produces at 120 BPM,
 *  used purely for the visual preview's scroll speed. Real audio uses
 *  the engine's actual BPM. */
function syncToHzAt120(idx: number): number {
  // Same as LfoRateSync::cycles_per_beat × (120/60 = 2 beats/s)
  const cyclesPerBeat = [0.25, 0.5, 1, 2, 4, 8][idx] ?? 1;
  return cyclesPerBeat * 2;
}
